import type { VatRateSchema } from "@repo/validators";
import type { z } from "zod";
import { useEffect } from "react";
import { Permissions } from "@repo/database";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { VatRate } from "~/providers/general-provider";
import { api } from "~/lib/api";
import { useVatRateStore } from "~/stores/use-vat-rate-store";
import { usePermissionManager } from "./use-permission-manager";

export const useVatRateManager = () => {
  const { mutate: createVatRate, isPending: isCreatingVatRate } =
    api.vatRate.createVatRate.useMutation();

  const { mutate: updateVatRate, isPending: isUpdatingVatRate } =
    api.vatRate.updateVatRate.useMutation();

  const isLoading = isCreatingVatRate || isUpdatingVatRate;

  const { setVatRateFormOpen, setSelectedVatRate, setIsLoading } =
    useVatRateStore();

  const { hasPermission } = usePermissionManager();

  const { t } = useTranslation("common");

  const createVatRateHandler = async (
    vatRate: z.infer<ReturnType<typeof VatRateSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_vat_rate,
      label: t("Permissions.create_vat_rate_label"),
    });

    if (!permission) return false;

    return new Promise<boolean>((resolve) => {
      createVatRate(vatRate, {
        onSuccess() {
          toast.success(t("create_item_success", { item: t("vat_rate") }));
          resolve(true);
        },
        onError() {
          toast.error(t("create_item_error", { item: t("vat_rate") }));
          resolve(false);
        },
      });
    });
  };

  const updateVatRateHandler = async (
    id: string,
    vatRate: z.infer<ReturnType<typeof VatRateSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_vat_rate,
      label: t("Permissions.update_vat_rate_label"),
    });

    if (!permission) return false;

    return new Promise<boolean>((resolve) => {
      updateVatRate(
        {
          id,
          ...vatRate,
        },
        {
          onSuccess() {
            toast.success(t("update_item_success", { item: t("vat_rate") }));
            resolve(true);
          },
          onError() {
            toast.error(t("update_item_error", { item: t("vat_rate") }));
            resolve(false);
          },
        },
      );
    });
  };

  const openVatRateForm = async (vatRate?: VatRate) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_vat_rate,
      label: t("Permissions.view_vat_rate_label"),
    });

    if (!permission) return false;

    setSelectedVatRate(vatRate);
    setVatRateFormOpen(true);
  };

  const getLoadingState = () => {
    return isLoading;
  };

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  return {
    createVatRate: createVatRateHandler,
    updateVatRate: updateVatRateHandler,
    getLoadingState,
    openVatRateForm,
  };
};
